"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { mod } from "@/lib/utils";
import { HERO_CONFIG } from "@/data/hero";
import type { Collection } from "@/types";

interface UseHeroSliderResult {
  activeIndex: number;
  prevIndex: number;
  active: Collection;
  goTo: (index: number, userInitiated?: boolean) => void;
  next: () => void;
  prev: () => void;
  duration: number;
}

// Roughly matches the crossfade duration in lib/animations.ts (0.9s fade
// + a little buffer) so autoplay can't fire again mid-transition.
const TRANSITION_LOCK_MS = 700;

export function useHeroSlider(
  collections: Collection[],
  initialIndex = 0
): UseHeroSliderResult {
  const total = collections.length;
  const [activeIndex, setActiveIndex] = useState(mod(initialIndex, total));
  const [prevIndex, setPrevIndex] = useState(mod(initialIndex, total));
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isTransitioning = useRef(false);
  const unlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startAutoplay = useCallback(() => {
    clearTimer();
    if (total <= 1) return; // nothing to autoplay
    timerRef.current = setInterval(() => {
      if (isTransitioning.current) return; // previously dead code — now enforced
      if (typeof document !== "undefined" && document.hidden) return;
      setActiveIndex((i) => {
        const nextIdx = mod(i + 1, total);
        setPrevIndex(i);
        return nextIdx;
      });
    }, HERO_CONFIG.autoplayDuration);
  }, [total, clearTimer]);

  useEffect(() => {
    startAutoplay();

    function handleVisibility() {
      if (document.hidden) {
        clearTimer();
      } else {
        startAutoplay();
      }
    }
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearTimer();
      document.removeEventListener("visibilitychange", handleVisibility);
      if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
    };
  }, [startAutoplay, clearTimer]);

  const lockTransition = useCallback(() => {
    isTransitioning.current = true;
    if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
    unlockTimerRef.current = setTimeout(() => {
      isTransitioning.current = false;
    }, TRANSITION_LOCK_MS);
  }, []);

  const goTo = useCallback(
    (index: number, userInitiated = true) => {
      const nextIdx = mod(index, total);
      setActiveIndex((current) => {
        if (current === nextIdx) return current;
        setPrevIndex(current);
        return nextIdx;
      });
      lockTransition();
      if (userInitiated) startAutoplay();
    },
    [total, startAutoplay, lockTransition]
  );

  const next = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
  const prev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

  return {
    activeIndex,
    prevIndex,
    active: collections[activeIndex] as Collection,
    goTo,
    next,
    prev,
    duration: HERO_CONFIG.autoplayDuration,
  };
}